import {
  HomeIcon,
  UsersIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  TrophyIcon,
  ChartBarIcon,
  GlobeAltIcon,
  BanknotesIcon,
  PaintBrushIcon,
  ViewColumnsIcon,
  UserPlusIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  AdjustmentsHorizontalIcon,
  MegaphoneIcon,
  ClockIcon,
  PresentationChartLineIcon,
  BookOpenIcon,
  TruckIcon,
  AcademicCapIcon,
  InboxArrowDownIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import { User, isAdmin, isStaff, isParent, isGuestOnly } from '../../types/auth';

export interface NavItem {
  name: string;
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

export interface NavGroup {
  title: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  items: NavItem[];
}

export const topNavItem: NavItem = { name: 'Áttekintő', href: '/', icon: HomeIcon };

/**
 * @param aiAvailable Be van-e kapcsolva az AI (useAiStatus) - az AI asszisztens
 * menüpont csak ilyenkor látszik, és csak a stábnak.
 */
export const getNavigationGroups = (
  user: User | null,
  membershipFeesEnabled: boolean,
  aiAvailable = false
): NavGroup[] => {
  // A vendég (gondnok, takarító, kitmanager, külsős csapat) a klubnaptárat
  // nézi - minden más menüpont üres lapra vinné.
  if (isGuestOnly(user)) {
    return [
      {
        title: 'Naptár',
        icon: CalendarDaysIcon,
        items: [
          { name: 'Naptár', href: '/calendar', icon: CalendarDaysIcon },
          { name: 'Foglalásaim', href: '/bookings', icon: ClockIcon },
          { name: 'Üzenőfal', href: '/board', icon: MegaphoneIcon },
        ],
      },
    ];
  }

  return [
    {
      title: 'Sport',
      icon: TrophyIcon,
      items: [
        { name: 'Naptár', href: '/calendar', icon: CalendarDaysIcon },
        { name: 'Események', href: '/events', icon: ClipboardDocumentListIcon },
        { name: 'Mérkőzések', href: '/matches', icon: TrophyIcon },
        { name: 'Mérkőzés statisztika', href: '/statistics', icon: ChartBarIcon },
        ...(isStaff(user) ? [{ name: 'Gyakorlattár', href: '/drills', icon: BookOpenIcon }] : []),
        { name: 'Edzés statisztika', href: '/training-statistics', icon: PresentationChartLineIcon },
        { name: 'Utazások', href: '/travel', icon: TruckIcon },
        ...(isStaff(user) && aiAvailable ? [{ name: 'AI asszisztens', href: '/ai', icon: SparklesIcon }] : []),
      ],
    },
    {
      title: 'Klub kezelése',
      icon: UserGroupIcon,
      items: [
        { name: 'Üzenőfal', href: '/board', icon: MegaphoneIcon },
        ...(isStaff(user) ? [{ name: 'Játékosok', href: '/players', icon: UsersIcon }] : []),
        ...(isAdmin(user) ? [{ name: 'Jelentkezések', href: '/applications', icon: InboxArrowDownIcon }] : []),
        ...(isAdmin(user) ? [{ name: 'Iskolák', href: '/schools', icon: AcademicCapIcon }] : []),
        ...(isAdmin(user) ? [{ name: 'Korosztályok', href: '/age-groups', icon: UserGroupIcon }] : []),
        // A tagdíj az admin dolga; az edző csak szülőként, a saját gyereke díjait látja
        ...(membershipFeesEnabled && (isAdmin(user) || isParent(user))
          ? [{ name: 'Tagdíjak', href: '/membership-fees', icon: BanknotesIcon }]
          : []),
        { name: 'Dokumentumok', href: '/documents', icon: DocumentTextIcon },
      ],
    },
    {
      title: 'Pályák',
      icon: BuildingOfficeIcon,
      items: isStaff(user)
        ? [
            // A pályák kezelése az adminé; a foglaltságot a stáb is látja
            ...(isAdmin(user) ? [{ name: 'Pályák', href: '/venues', icon: BuildingOfficeIcon }] : []),
            { name: 'Pálya foglaltság', href: '/venue-occupancy', icon: ViewColumnsIcon },
            ...(isAdmin(user)
              ? [{ name: 'Foglalás jóváhagyás', href: '/bookings', icon: ClockIcon }]
              : []),
          ]
        : [],
    },
    {
      title: 'Rendszer',
      icon: Cog6ToothIcon,
      items: isAdmin(user)
        ? [
            { name: 'Felhasználók', href: '/users', icon: UsersIcon },
            { name: 'Regisztrációk', href: '/registrations', icon: UserPlusIcon },
            { name: 'Email napló', href: '/email-log', icon: EnvelopeIcon },
            { name: 'IFA', href: '/ifa', icon: GlobeAltIcon },
            { name: 'Testreszabás', href: '/club-settings', icon: PaintBrushIcon },
            { name: 'Rendszerbeállítások', href: '/system-settings', icon: AdjustmentsHorizontalIcon },
          ]
        : [],
    },
  ].filter((g) => g.items.length > 0);
};
